import React, { useState } from 'react';
import { Sparkles, CheckCircle2, AlertCircle, RefreshCw, Zap, Clock, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { formatVND } from '../utils/format';

interface ParsedPrice {
  code: string;
  price: number;
  matched: boolean;
  assetName?: string;
  currentPrice?: number;
  change?: number;
  changePct?: number;
}

function parseSnippet(text: string, assets: { code: string; name: string; currentPrice: number }[]): ParsedPrice[] {
  const results: ParsedPrice[] = [];
  const seen = new Set<string>();

  const patterns = [
    /\b([A-Z]{2,6})\s*[:\-=]\s*([\d,\.]+)/gi,
    /([A-Z]{2,6})\s+([\d,\.]+)\s*(?:đ|VND|vnđ|đồng)?/gi,
    /giá\s+([A-Z]{2,6})\s+(?:là\s+|:\s*)?([\d,\.]+)/gi,
    /([A-Z]{2,6})\s+(?:giá|price|close|đóng cửa)[:\s]+([\d,\.]+)/gi,
  ];

  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      const code = match[1].toUpperCase();
      const raw = match[2].replace(/\./g, '').replace(/,/g, '');
      const price = parseFloat(raw);
      if (!code || isNaN(price) || price <= 0 || seen.has(code)) continue;
      seen.add(code);

      const asset = assets.find(a => a.code.toUpperCase() === code);
      results.push({
        code, price, matched: !!asset,
        assetName: asset?.name,
        currentPrice: asset?.currentPrice,
        change: asset ? price - asset.currentPrice : undefined,
        changePct: asset ? ((price - asset.currentPrice) / asset.currentPrice) * 100 : undefined,
      });
    }
  }
  return results;
}

const SAMPLES = [
  { label: 'Format 1', text: `QNS: 58,000\nVEA: 51,500\nSZB: 31,200\nACB: 23,800` },
  { label: 'Format 2', text: `Bảng giá 18/03:\n- QNS đóng cửa 59,200đ\n- VEA tăng lên 52,000\n- ACB: 24,100` },
  { label: 'Format 3', text: `QNS=58500, VEA=51800, SZB=32000, ACB=24500` },
];

export function AISnippetParser() {
  const { state, dispatch } = useAppState();
  const [snippet, setSnippet] = useState('');
  const [parsed, setParsed] = useState<ParsedPrice[]>([]);
  const [applied, setApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const assetInfo = state.assets.map(a => ({ code: a.code, name: a.name, currentPrice: a.currentPrice }));
  const matchedCount = parsed.filter(p => p.matched).length;

  const handleParse = () => {
    if (!snippet.trim()) return;
    setLoading(true); setApplied(false);
    setTimeout(() => { setParsed(parseSnippet(snippet, assetInfo)); setLoading(false); }, 500);
  };

  const handleApply = () => {
    const updates = parsed.filter(p => p.matched).map(p => ({ code: p.code, price: p.price }));
    if (!updates.length) return;
    dispatch({ type: 'BULK_UPDATE_PRICES', payload: updates });
    setApplied(true);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <div className="grid-cols-2-md">

        {/* Input panel */}
        <Card>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--bd)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: 'linear-gradient(135deg, rgba(16,185,129,0.25) 0%, rgba(16,185,129,0.1) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={14} color="#10b981" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Vùng dán nội dung</div>
                <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>Paste bất kỳ bảng giá nào — AI tự phân tích</div>
              </div>
            </div>
          </div>
          <CardBody>
            <textarea
              value={snippet}
              onChange={(e) => setSnippet(e.target.value)}
              placeholder={'VD:\nQNS: 58,000\nVEA: 51,500\nACB đóng cửa 23,800đ'}
              style={{
                width: '100%', height: 200, padding: '12px 14px',
                borderRadius: 10, fontSize: 13, outline: 'none', resize: 'none',
                fontFamily: '"SF Mono", "Fira Code", monospace',
                lineHeight: 1.7,
                backgroundColor: 'var(--bg-input)', border: '1px solid var(--bd)', color: 'var(--tx-1)',
              }}
            />

            <div style={{ marginTop: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--tx-4)', marginBottom: 7 }}>Thử mẫu nhanh:</div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {SAMPLES.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => setSnippet(s.text)}
                    style={{
                      padding: '5px 11px', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                      background: 'var(--bd)', color: 'var(--tx-3)',
                      border: '1px solid var(--bd)', transition: 'all 0.1s',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-3)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-el)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-3)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--bd)'; }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <Button
              variant="primary"
              style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
              icon={loading ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={14} />}
              onClick={handleParse}
              disabled={!snippet.trim() || loading}
            >
              {loading ? 'Đang phân tích...' : 'Phân tích & Trích xuất'}
            </Button>
          </CardBody>
        </Card>

        {/* Results panel */}
        <Card>
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>Kết quả phân tích</div>
            {parsed.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>{matchedCount}</span>/{parsed.length} tìm thấy
                </span>
                {!applied && matchedCount > 0 && (
                  <Button variant="primary" size="xs" onClick={handleApply}>Áp dụng {matchedCount} giá</Button>
                )}
                {applied && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#10b981' }}>
                    <CheckCircle2 size={13} />
                    Đã cập nhật
                  </div>
                )}
              </div>
            )}
          </div>
          <CardBody style={{ minHeight: 300 }}>
            {parsed.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--bd-sub)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <Sparkles size={22} color="var(--tx-5)" />
                </div>
                <div style={{ fontSize: 13, color: 'var(--tx-5)', marginBottom: 4 }}>Kết quả sẽ hiển thị ở đây</div>
                <div style={{ fontSize: 11, color: 'var(--tx-5)' }}>Paste dữ liệu và nhấn phân tích</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {parsed.map((p) => (
                  <div
                    key={p.code}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 10,
                      background: p.matched ? 'rgba(16,185,129,0.05)' : 'var(--bg-card-el)',
                      border: `1px solid ${p.matched ? 'rgba(16,185,129,0.15)' : 'var(--bd)'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {p.matched
                        ? <CheckCircle2 size={15} color="#10b981" />
                        : <AlertCircle size={15} color="var(--tx-4)" />
                      }
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: p.matched ? 'var(--tx-1)' : 'var(--tx-3)' }}>{p.code}</div>
                        {p.assetName && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>{p.assetName}</div>}
                        {!p.matched && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>Không tìm thấy tài sản</div>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx-1)' }}>{formatVND(p.price)}</div>
                      {p.matched && p.change !== undefined && (
                        <div style={{ fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, marginTop: 2, color: p.change >= 0 ? '#10b981' : '#f87171' }}>
                          {p.change >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
                          {p.change >= 0 ? '+' : ''}{formatVND(p.change)} · {p.changePct?.toFixed(2)}%
                        </div>
                      )}
                      {p.matched && <div style={{ fontSize: 10, color: 'var(--tx-4)', marginTop: 1 }}>Hiện: {formatVND(p.currentPrice!)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Price history */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={13} color="var(--tx-4)" />
          <span style={{ fontSize: 12, color: 'var(--tx-4)' }}>Lịch sử cập nhật giá</span>
        </div>
        {state.priceHistory.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', fontSize: 12, color: 'var(--tx-5)' }}>
            Chưa có lịch sử
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                {['Thời gian', 'Mã tài sản', 'Giá mới', 'Nguồn'].map((h, i) => (
                  <th key={h} style={{ padding: '9px 16px', textAlign: i > 1 ? 'right' as const : 'left' as const, fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...state.priceHistory].reverse().slice(0, 15).map((ph) => {
                const asset = state.assets.find(a => a.id === ph.assetId);
                return (
                  <tr key={ph.id} style={{ borderBottom: '1px solid var(--bd-sub)' }}>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--tx-4)' }}>
                      {new Date(ph.date).toLocaleString('vi-VN')}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-1)' }}>{asset?.code || ph.assetId}</span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#10b981' }}>
                      {formatVND(ph.price)}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <span style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 5, fontWeight: 500,
                        background: ph.source === 'ai-snippet' ? 'rgba(16,185,129,0.1)' : 'rgba(56,189,248,0.1)',
                        color: ph.source === 'ai-snippet' ? '#10b981' : '#38bdf8',
                      }}>
                        {ph.source === 'ai-snippet' ? 'AI Snippet' : 'Thủ công'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
